import { type ValidationError } from "~/gql/graphqlBase.generated";
import { UserInviteAdminFragment } from "~/gql/queries/users.generated";
import snackMessages from "~/helpers/snackMessages";
import i18n from "~/i18n/i18n";

export const UserInviteStatusCode = {
	Unused: 0,
	Accepted: 1,
	Used: 2,
	Failed: 3
} as const;

export const UserInviteEmailStatusCode = {
	PendingSend: 0,
	Sent: 1,
	DeliveryFailed: 2
} as const;

export type InviteDisplayStatus =
	| "unused"
	| "used"
	| "expired"
	| "alreadyRegistered"
	| "deliveryFailed"
	| "failed"
	| "sending"
	| "sendDelayed";

export type InviteStatusSource = Pick<
	UserInviteAdminFragment,
	"createdAt" | "emailError" | "emailStatus" | "expiresAt" | "resentAt" | "status"
>;

export const INVITE_SEND_DELAY_MS = 15 * 60 * 1000;

export interface InviteStatusDescriptor {
	color: "default" | "error" | "success" | "warning";
	dark: boolean;
	label: string;
	reason?: string;
	status: InviteDisplayStatus;
}

const ALREADY_REGISTERED_REASON =
	"This email already has an account. Link or assign the existing user instead of sending a new invite.";
const GENERIC_DELIVERY_FAILURE_REASON = "The invite email could not be delivered.";

const isAlreadyRegisteredEmailError = (emailError: string): boolean => /already exists/i.test(emailError);

const DESCRIPTORS: Record<Exclude<InviteDisplayStatus, "sending" | "sendDelayed">, InviteStatusDescriptor> = {
	alreadyRegistered: { color: "warning", dark: false, label: "Already has an account", status: "alreadyRegistered" },
	deliveryFailed: { color: "error", dark: true, label: "Delivery Failed", status: "deliveryFailed" },
	expired: { color: "error", dark: false, label: "Expired", status: "expired" },
	failed: { color: "error", dark: true, label: "Failed", status: "failed" },
	unused: { color: "default", dark: false, label: "Unused", status: "unused" },
	used: { color: "success", dark: false, label: "Used", status: "used" }
};

export const isInviteEmailDeliveryFailure = (
	descriptor: InviteStatusDescriptor | null | undefined
): descriptor is InviteStatusDescriptor =>
	descriptor?.status === "deliveryFailed" || descriptor?.status === "alreadyRegistered";

const isWithinSendLease = (invite: Pick<InviteStatusSource, "createdAt" | "resentAt">, now: Date): boolean =>
	now.getTime() - new Date(invite.resentAt ?? invite.createdAt).getTime() < INVITE_SEND_DELAY_MS;

const sendStatusLabel = (key: string): string => i18n.t(`admin.users.inviteStatus.${key}`, { ns: "common" });

const sendingDescriptor = (): InviteStatusDescriptor => ({
	color: "default",
	dark: false,
	label: sendStatusLabel("sending"),
	status: "sending"
});

const sendDelayedDescriptor = (): InviteStatusDescriptor => ({
	color: "warning",
	dark: false,
	label: sendStatusLabel("sendDelayed"),
	reason: sendStatusLabel("sendDelayedReason"),
	status: "sendDelayed"
});

export const deriveInviteDisplayStatus = (
	invite: InviteStatusSource,
	now: Date = new Date()
): InviteStatusDescriptor => {
	if (invite.status === UserInviteStatusCode.Failed) return DESCRIPTORS.failed;
	if (invite.status === UserInviteStatusCode.Used || invite.status === UserInviteStatusCode.Accepted) {
		return DESCRIPTORS.used;
	}
	if (invite.emailStatus === UserInviteEmailStatusCode.DeliveryFailed) {
		if (isAlreadyRegisteredEmailError(invite.emailError)) {
			return { ...DESCRIPTORS.alreadyRegistered, reason: ALREADY_REGISTERED_REASON };
		}
		return { ...DESCRIPTORS.deliveryFailed, reason: invite.emailError || GENERIC_DELIVERY_FAILURE_REASON };
	}
	const expiresAt = invite.expiresAt ? new Date(invite.expiresAt) : null;
	if (expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt.getTime() < now.getTime()) {
		return DESCRIPTORS.expired;
	}
	if (invite.emailStatus === UserInviteEmailStatusCode.PendingSend) {
		return isWithinSendLease(invite, now) ? sendingDescriptor() : sendDelayedDescriptor();
	}
	return DESCRIPTORS.unused;
};

export const isInviteSending = (invite: InviteStatusSource, now: Date = new Date()): boolean =>
	deriveInviteDisplayStatus(invite, now).status === "sending";

export const buildInviteSetupLink = (code: string): string =>
	`${window.location.origin}/register/${encodeURIComponent(code)}`;

const partitionExpiredLast = <T>(items: T[], isExpired: (item: T) => boolean): T[] => {
	const expired = items.filter(isExpired);
	if (expired.length === 0) return items;
	const expiredSet = new Set(expired);
	return [...items.filter((item) => !expiredSet.has(item)), ...expired];
};

export const sortInvitesForDisplay = <T extends InviteStatusSource & Pick<UserInviteAdminFragment, "recipientEmail">>(
	invites: T[]
): T[] => {
	const byRecipientEmail = (a: T, b: T) => a.recipientEmail.localeCompare(b.recipientEmail);
	const isExpired = (invite: T) => deriveInviteDisplayStatus(invite).status === "expired";
	return partitionExpiredLast([...invites].sort(byRecipientEmail), isExpired);
};

export const moveExpiredNodesLast = <T extends { data?: InviteStatusSource | null }>(nodes: T[]): T[] =>
	partitionExpiredLast(nodes, (node) => !!node.data && deriveInviteDisplayStatus(node.data).status === "expired");

export const EMAIL_ALREADY_HAS_ACCOUNT_CODE = "EMAIL_ALREADY_HAS_ACCOUNT";

export type InviteMutationError = Pick<ValidationError, "code" | "message">;

export const findAlreadyHasAccountMessage = (errors?: Maybe<InviteMutationError[]>): string | undefined =>
	errors?.find((error) => error.code === EMAIL_ALREADY_HAS_ACCOUNT_CODE)?.message;

export const inviteRefusalMessage = (errors?: Maybe<InviteMutationError[]>): string =>
	findAlreadyHasAccountMessage(errors) || errors?.[0]?.message || snackMessages.createError("user invite");
